import { z } from 'zod';
import { REPORT_CLASSES } from './report-catalog.js';
import { ReportPresentationOptionsSchema } from './report-chrome.js';
import { REPORT_PROFILE_OPTION_KEYS } from './report-profile-options.js';

export const REPORT_PROFILE_SCHEMA_VERSION = 2;
export const REPORT_PROFILE_TOP_N_LIMIT = 1_000;

const id = z.string().trim().min(1).max(120);
const objectId = z.string().trim().regex(
    /^[a-f0-9]{24}$/i,
    'A persisted document classification id is required',
);
const stableKey = z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9-]*$/);
const reportSelectorScalar = z.union([
    z.string().max(500),
    z.number().finite(),
    z.boolean(),
]);
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const uniqueIds = z.array(id).max(500).refine(
    (values) => new Set(values).size === values.length,
    { message: 'Values must be unique' },
);

function boundedObject(maxKeys, maxBytes) {
    return z.record(z.string().trim().min(1).max(120), z.unknown()).superRefine((value, ctx) => {
        if (Object.keys(value).length > maxKeys) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `At most ${maxKeys} values are allowed` });
        }
        try {
            if (new TextEncoder().encode(JSON.stringify(value)).byteLength > maxBytes) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Value exceeds ${maxBytes} bytes` });
            }
        } catch {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Value must be JSON serializable' });
        }
    });
}

export const ReportProfileOptionsSchema = z.object({
    sectionVisibility: z.record(stableKey, z.boolean()).optional(),
    sectionOrder: z.array(stableKey).max(100).refine(
        (values) => new Set(values).size === values.length,
        { message: 'Section order must be unique' },
    ).optional(),
    visibleFields: z.array(z.string().trim().min(1).max(120)).max(100).refine(
        (values) => new Set(values).size === values.length,
        { message: 'Visible fields must be unique' },
    ).optional(),
    chartVisibility: z.record(stableKey, z.boolean()).optional(),
    grouping: z.array(z.string().trim().min(1).max(120)).max(2).optional(),
    tableDensity: z.enum(['compact', 'standard', 'comfortable']).optional(),
    topN: z.number().int().positive().max(REPORT_PROFILE_TOP_N_LIMIT).optional(),
    narrativeSections: z.array(stableKey).max(50).refine(
        (values) => new Set(values).size === values.length,
        { message: 'Narrative sections must be unique' },
    ).optional(),
    presentation: ReportPresentationOptionsSchema.optional(),
}).strict();

export const ReportProfileConfidentialitySchema = z.object({
    classId: objectId,
    resolvedSnapshot: z.object({
        code: z.string().trim().min(1).max(80),
        label: z.string().trim().min(1).max(160),
        level: z.number().int().min(0).max(1000),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/).transform((value) => value.toLowerCase()),
        watermark: z.object({
            enabled: z.boolean(),
            text: z.string().trim().max(160),
        }).strict().optional(),
    }).strict(),
}).strict();

const reportProfileWritableShape = {
    schemaVersion: z.literal(REPORT_PROFILE_SCHEMA_VERSION),
    reportKey: stableKey,
    definitionVersionAtSave: z.number().int().positive(),
    profileContract: z.object({
        key: stableKey,
        version: z.number().int().positive(),
    }).strict(),
    name: z.string().trim().min(1).max(160),
    locale: z.enum(['tr', 'en']),
    status: z.enum(['draft', 'active', 'inactive']),
    scope: z.object({
        mode: z.enum(['filtered', 'selected', 'single', 'tenant']),
        values: boundedObject(50, 32_768).default({}),
    }).strict(),
    selectorDefaults: boundedObject(50, 16_384).default({}),
    /** Legacy profile output marking; new reports use the owning document classification. */
    confidentiality: ReportProfileConfidentialitySchema.optional(),
    options: ReportProfileOptionsSchema,
    sharing: z.object({
        visibility: z.enum(['private', 'shared', 'tenant']),
        userIds: uniqueIds,
    }).strict(),
    isDefault: z.boolean(),
    customFields: boundedObject(100, 65_536).default({}),
};

export const CreateReportProfileSchema = z.object(reportProfileWritableShape).strict();

export const ReportProfilePublicWriteSchema = CreateReportProfileSchema.extend({
    profileVersion: z.number().int().positive(),
}).strict();

export const UpdateReportProfileSchema = z.object({
    expectedVersion: z.number().int().positive(),
    ...Object.fromEntries(Object.entries(reportProfileWritableShape).map(([key, schema]) => [key, schema.optional()])),
}).strict().refine(
    (value) => Object.keys(value).some((key) => key !== 'expectedVersion'),
    { message: 'At least one profile field must be updated' },
);

export const ReportProfileMigrationAuditSchema = z.object({
    sourceSchemaVersion: z.number().int().positive(),
    legacyOptions: z.unknown().optional(),
    legacyBrandingDigest: digest.optional(),
    migratedAt: z.coerce.date(),
    migrationOperationId: id,
}).strict();

export const ReportProfileSchema = ReportProfilePublicWriteSchema.extend({
    id: id,
    tenant: id,
    owner: id,
    createdBy: id,
    updatedBy: id,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    migrationAudit: ReportProfileMigrationAuditSchema.optional(),
}).strict();

export const ReportProfilePublicSchema = ReportProfileSchema.omit({ migrationAudit: true });

export const ReportProfileIndexDefinitionSchema = z.object({
    reportKey: stableKey,
    definitionVersion: z.number().int().positive(),
    surfaceKey: stableKey,
    domainKey: stableKey,
    reportClass: z.enum(REPORT_CLASSES),
    labelKey: z.string().trim().min(1).max(160),
    scopeModes: z.array(z.enum(['filtered', 'selected', 'single', 'tenant'])).min(1),
    selectorDefaults: z.record(
        z.string().trim().min(1).max(120),
        reportSelectorScalar,
    ).default({}),
    selectorSchema: z.object({
        properties: z.record(z.string(), z.string()),
        required: z.array(z.string()),
        additionalProperties: z.literal(false),
    }).strict(),
    profileContract: z.object({ key: stableKey, version: z.number().int().positive() }).strict(),
    optionKeys: z.array(z.enum(REPORT_PROFILE_OPTION_KEYS)).max(REPORT_PROFILE_OPTION_KEYS.length)
        .refine((values) => new Set(values).size === values.length, { message: 'Option keys must be unique' }),
    visibleFields: z.array(z.string().trim().min(1).max(120)).min(1).max(100)
        .refine((values) => new Set(values).size === values.length, { message: 'Visible fields must be unique' }),
    lifecycle: z.object({
        runEnabled: z.boolean(),
        scheduleEnabled: z.boolean(),
    }).strict(),
    capabilities: z.object({
        profile: z.boolean(),
        profileOptional: z.boolean(),
        schedule: z.boolean(),
        snapshot: z.boolean(),
        compare: z.boolean(),
        aiNarrative: z.boolean(),
        designer: z.boolean(),
    }).strict(),
    launchContext: z.object({
        sourcePageKey: z.string().trim().min(1).max(160),
        contextual: z.boolean(),
    }).strict(),
    profiles: z.array(ReportProfilePublicSchema),
}).strict();

export const ReportProfileIndexSchema = z.object({
    definitions: z.array(ReportProfileIndexDefinitionSchema),
}).strict();
